import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useMemo } from "react";
import { Text, Badge, Button, Spinner, Input, MessageBar, MessageBarBody, Card, } from "@fluentui/react-components";
import { SearchRegular, ArrowSyncRegular, ClockRegular, PersonRegular, CheckmarkCircleFilled, ArrowExitRegular, } from "@fluentui/react-icons";
import { useAuth } from "../../../context/AuthContext";
import { getBadgeagesByEmployee } from "../../../services/dataService";
function timeToSec(t) {
    if (!t)
        return 0;
    const [h, m] = t.split(":").map(Number);
    return h * 3600 + m * 60;
}
function calcStatut(b) {
    // Si entree2 sans sortie2 → présent (revenu de pause)
    if (b.entree2 && !b.sortie2)
        return "present";
    // Si entree1 et sortie1 mais pas entree2 → en pause
    if (b.entree1 && b.sortie1 && !b.entree2)
        return "pause";
    // Si entree2 et sortie2 → parti
    if (b.entree2 && b.sortie2)
        return "parti";
    // Si entree1 sans sortie1 → présent
    if (b.entree1 && !b.sortie1)
        return "present";
    // Si entree1 et sortie1 sans entree2 (et journée continue type matin uniquement)
    if (b.entree1 && b.sortie1)
        return "parti";
    return "parti";
}
function getDerniereActivite(b) {
    // Trouver l'heure la plus récente parmi les 4
    let max = "";
    let maxSec = 0;
    for (const h of [b.entree1, b.sortie1, b.entree2, b.sortie2]) {
        if (h) {
            const sec = timeToSec(h);
            if (sec > maxSec) {
                maxSec = sec;
                max = h;
            }
        }
    }
    return { heure: max, sec: maxSec };
}
export const PresenceJourView = () => {
    const { apiToken: accessToken } = useAuth();
    const [badgeages, setBadgeages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [selectedDate, setSelectedDate] = useState(() => {
        // Par défaut : aujourd'hui (en heure locale)
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    });
    const fetchData = useCallback(async () => {
        if (!accessToken)
            return;
        setLoading(true);
        setError(null);
        try {
            // Filtrage par date côté backend pour ne ramener que ce qu'il faut
            const data = await getBadgeagesByEmployee(accessToken, "", undefined, selectedDate);
            setBadgeages(data);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Erreur");
        }
        finally {
            setLoading(false);
        }
    }, [accessToken, selectedDate]);
    useEffect(() => {
        fetchData();
    }, [fetchData]);
    // Construire la liste des salariés présents avec leur statut
    const salaries = useMemo(() => {
        return badgeages.map((b) => {
            const last = getDerniereActivite(b);
            return {
                email: b.employeeEmail,
                name: b.employeeName,
                entree1: b.entree1 || "",
                sortie1: b.sortie1 || "",
                entree2: b.entree2 || "",
                sortie2: b.sortie2 || "",
                source: b.source || "",
                statut: calcStatut(b),
                derniereActivite: last.heure,
                derniereActiviteSec: last.sec,
            };
        });
    }, [badgeages]);
    // Filtre par recherche + tri par dernière activité (plus récent en premier)
    const filtered = useMemo(() => {
        const s = search.toLowerCase().trim();
        return salaries
            .filter((sal) => {
            if (!s)
                return true;
            return (sal.name.toLowerCase().includes(s) ||
                sal.email.toLowerCase().includes(s));
        })
            .sort((a, b) => b.derniereActiviteSec - a.derniereActiviteSec);
    }, [salaries, search]);
    const stats = useMemo(() => {
        const presents = salaries.filter((s) => s.statut === "present").length;
        const pauses = salaries.filter((s) => s.statut === "pause").length;
        const partis = salaries.filter((s) => s.statut === "parti").length;
        return { presents, pauses, partis, total: salaries.length };
    }, [salaries]);
    const formatDateLong = (iso) => {
        // iso au format YYYY-MM-DD → on construit en heure locale (midi pour éviter timezone)
        const d = new Date(iso + "T12:00:00");
        return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    };
    const todayIso = (() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    })();
    const isToday = selectedDate === todayIso;
    const statutBadge = (statut) => {
        if (statut === "present") {
            return (_jsx(Badge, { appearance: "filled", color: "success", icon: _jsx(CheckmarkCircleFilled, {}), children: "Pr\u00E9sent" }));
        }
        if (statut === "pause") {
            return (_jsx(Badge, { appearance: "filled", color: "warning", icon: _jsx(ClockRegular, {}), children: "Pause d\u00E9jeuner" }));
        }
        return (_jsx(Badge, { appearance: "tint", color: "subtle", icon: _jsx(ArrowExitRegular, {}), children: "Parti" }));
    };
    return (_jsxs("div", { children: [_jsxs("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px", flexWrap: "wrap", gap: "12px" }, children: [_jsxs("div", { children: [_jsx(Text, { size: 500, weight: "bold", style: { display: "block" }, children: isToday ? "Présence du jour" : "Présence — jour sélectionné" }), _jsx(Text, { size: 200, style: { color: "#666", textTransform: "capitalize" }, children: formatDateLong(selectedDate) })] }), _jsxs("div", { style: { display: "flex", gap: "8px", alignItems: "center" }, children: [_jsx(Input, { type: "date", value: selectedDate, onChange: (_, d) => setSelectedDate(d.value), max: todayIso, style: { width: "170px" } }), !isToday && (_jsx(Button, { size: "small", appearance: "subtle", onClick: () => setSelectedDate(todayIso), children: "Aujourd'hui" })), _jsx(Button, { icon: _jsx(ArrowSyncRegular, {}), appearance: "subtle", onClick: fetchData, children: "Actualiser" })] })] }), _jsxs("div", { style: { display: "flex", gap: "12px", marginBottom: "16px", flexWrap: "wrap" }, children: [_jsxs(Card, { style: { flex: 1, minWidth: "150px", padding: "16px", borderLeft: "4px solid #10b981" }, children: [_jsx(Text, { size: 200, style: { color: "#666" }, children: "Pr\u00E9sents" }), _jsx("br", {}), _jsx(Text, { size: 700, weight: "bold", style: { color: "#10b981" }, children: stats.presents })] }), _jsxs(Card, { style: { flex: 1, minWidth: "150px", padding: "16px", borderLeft: "4px solid #f59e0b" }, children: [_jsx(Text, { size: 200, style: { color: "#666" }, children: "En pause" }), _jsx("br", {}), _jsx(Text, { size: 700, weight: "bold", style: { color: "#f59e0b" }, children: stats.pauses })] }), _jsxs(Card, { style: { flex: 1, minWidth: "150px", padding: "16px", borderLeft: "4px solid #6b7280" }, children: [_jsx(Text, { size: 200, style: { color: "#666" }, children: "Partis" }), _jsx("br", {}), _jsx(Text, { size: 700, weight: "bold", style: { color: "#6b7280" }, children: stats.partis })] }), _jsxs(Card, { style: { flex: 1, minWidth: "150px", padding: "16px", borderLeft: "4px solid #0078d4" }, children: [_jsx(Text, { size: 200, style: { color: "#666" }, children: "Total badg\u00E9s" }), _jsx("br", {}), _jsx(Text, { size: 700, weight: "bold", style: { color: "#0078d4" }, children: stats.total })] })] }), error && (_jsx(MessageBar, { intent: "error", style: { marginBottom: "12px" }, children: _jsx(MessageBarBody, { children: error }) })), _jsx(Input, { contentBefore: _jsx(SearchRegular, {}), value: search, onChange: (_, d) => setSearch(d.value), placeholder: "Rechercher un salari\u00E9 par nom ou email...", style: { width: "100%", maxWidth: "500px", marginBottom: "16px" } }), loading ? (_jsx(Spinner, { label: "Chargement des badgeages du jour..." })) : filtered.length === 0 ? (_jsxs("div", { style: { textAlign: "center", padding: "48px", color: "#9ca3af" }, children: [_jsx(ClockRegular, { style: { fontSize: 48, marginBottom: "12px" } }), _jsx("br", {}), _jsx(Text, { size: 400, children: salaries.length === 0
                            ? "Aucun salarié n'a encore badgé aujourd'hui"
                            : "Aucun résultat pour cette recherche" })] })) : (_jsx("div", { style: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "12px" }, children: filtered.map((sal) => (_jsxs(Card, { style: {
                        padding: "14px 16px",
                        borderLeft: `4px solid ${sal.statut === "present" ? "#10b981" : sal.statut === "pause" ? "#f59e0b" : "#9ca3af"}`,
                    }, children: [_jsxs("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }, children: [_jsxs("div", { style: { flex: 1, minWidth: 0 }, children: [_jsx(Text, { weight: "semibold", size: 300, style: { display: "block" }, children: sal.name }), _jsx(Text, { size: 100, style: { color: "#888" }, children: sal.email })] }), statutBadge(sal.statut)] }), _jsxs("div", { style: { display: "flex", gap: "12px", flexWrap: "wrap", fontSize: "12px", color: "#444" }, children: [sal.entree1 && (_jsxs("span", { children: [_jsx(PersonRegular, { style: { fontSize: 12 } }), " ", _jsx("strong", { children: sal.entree1 })] })), sal.sortie1 && (_jsxs("span", { children: [_jsx(ArrowExitRegular, { style: { fontSize: 12 } }), " ", sal.sortie1] })), sal.entree2 && (_jsxs("span", { children: [_jsx(PersonRegular, { style: { fontSize: 12 } }), " ", sal.entree2] })), sal.sortie2 && (_jsxs("span", { children: [_jsx(ArrowExitRegular, { style: { fontSize: 12 } }), " ", sal.sortie2] }))] }), sal.source && (_jsx("div", { style: { marginTop: "6px" }, children: _jsx(Badge, { size: "small", appearance: "outline", color: sal.source === "Kelio" ? "brand" : "informative", children: sal.source === "Kelio" ? "Badgeuse" : "Saisie manuelle" }) }))] }, sal.email))) }))] }));
};
